/**
 * Plotjack audio: baked ElevenLabs music loops, sound effects and narrator lines played through Web Audio.
 * Nothing loads until the player turns sound on (a user gesture unlocks the AudioContext), and each file is
 * fetched lazily the first time it is needed. Opus in WebM where the browser decodes it, AAC m4a otherwise (iOS).
 */
import { getPrefs } from './prefs';

export type SfxId = 'card_draw' | 'card_hover' | 'card_fly' | 'card_land' | 'glitch' | 'page_turn' | 'ui_click' | 'meter_up' | 'meter_down';
export type MusicId = 'title' | 'calm' | 'tense' | 'chaos' | 'ending';

interface Manifest {
  music: Record<string, number>;
  sfx: Record<string, number>;
  vo: Record<string, { id: string; dur: number }>;
}

const BASE = '/audio/';
const FADE = 2.2;
const LOOP_XFADE = 3;
const DUCK = 0.3;

/** FNV-1a 32 of the normalised line; matches scripts/audio/gen.py so displayed text finds its baked VO. */
export function lineHash(text: string): string {
  const norm = text.replace(/\s+/g, ' ').trim().toLowerCase();
  const bytes = new TextEncoder().encode(norm);
  let h = 0x811c9dc5;
  for (const b of bytes) {
    h ^= b;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

class AudioEngine {
  ctx: AudioContext | null = null;
  private master!: GainNode;
  private musicBus!: GainNode;
  private duckBus!: GainNode;
  private sfxBus!: GainNode;
  private voiceBus!: GainNode;
  private manifest: Promise<Manifest | null> | null = null;
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private ext: 'webm' | 'm4a' = 'webm';
  private music: { id: MusicId; gain: GainNode; src: AudioBufferSourceNode; timer: number } | null = null;
  private wantMusic: MusicId | null = null;
  private voice: { src: AudioBufferSourceNode; hash: string } | null = null;
  private lastSfx = new Map<string, number>();
  private listeners = new Set<() => void>();

  get unlocked() {
    return !!this.ctx && this.ctx.state === 'running';
  }

  /** Create and resume the AudioContext. Call synchronously inside a tap or click handler. */
  unlock() {
    if (typeof window === 'undefined') return;
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor({ latencyHint: 'interactive' });
      const c = this.ctx;
      this.master = c.createGain();
      this.master.connect(c.destination);
      this.musicBus = c.createGain();
      this.duckBus = c.createGain();
      this.musicBus.connect(this.duckBus).connect(this.master);
      this.sfxBus = c.createGain();
      this.sfxBus.connect(this.master);
      this.voiceBus = c.createGain();
      this.voiceBus.connect(this.master);
      const a = document.createElement('audio');
      this.ext = a.canPlayType('audio/webm; codecs="opus"') ? 'webm' : 'm4a';
      // a silent tick fully unlocks output on iOS
      const b = c.createBuffer(1, 1, 22050);
      const s = c.createBufferSource();
      s.buffer = b;
      s.connect(c.destination);
      s.start(0);
    }
    void this.ctx.resume();
    this.applyVolumes();
    if (!this.manifest) this.manifest = fetch(BASE + 'manifest.json').then((r) => (r.ok ? (r.json() as Promise<Manifest>) : null)).catch(() => null);
    if (this.wantMusic) this.setMusic(this.wantMusic);
  }

  /** Apply the stored volumes and mute state. */
  applyVolumes() {
    if (!this.ctx) return;
    const p = getPrefs();
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(p.sound ? 1 : 0, t, 0.08);
    this.musicBus.gain.setTargetAtTime(p.music, t, 0.08);
    this.sfxBus.gain.setTargetAtTime(p.sfx, t, 0.05);
    this.voiceBus.gain.setTargetAtTime(p.voice, t, 0.05);
    if (!p.sound) this.stopVoice();
  }

  private load(kind: string, id: string): Promise<AudioBuffer | null> {
    const key = `${kind}/${id}`;
    let p = this.buffers.get(key);
    if (!p) {
      const get = (ext: string) =>
        fetch(`${BASE}${kind}/${id}.${ext}`)
          .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
          .then((buf) => this.ctx!.decodeAudioData(buf));
      p = get(this.ext)
        .catch(() => (this.ext === 'webm' ? get('m4a') : Promise.reject(new Error('decode'))))
        .catch(() => null);
      this.buffers.set(key, p);
    }
    return p;
  }

  private active() {
    return !!this.ctx && getPrefs().sound;
  }

  playSfx(id: SfxId, opts: { volume?: number; rate?: number; minGapMs?: number } = {}) {
    if (!this.active()) return;
    const now = performance.now();
    if (now - (this.lastSfx.get(id) ?? 0) < (opts.minGapMs ?? 60)) return;
    this.lastSfx.set(id, now);
    void this.load('sfx', id).then((buf) => {
      if (!buf || !this.ctx) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = (opts.rate ?? 1) * (0.96 + Math.random() * 0.08);
      const g = this.ctx.createGain();
      g.gain.value = opts.volume ?? 1;
      src.connect(g).connect(this.sfxBus);
      src.start();
    });
  }

  /** Crossfade to a mood loop. Remembered until sound is unlocked. */
  setMusic(id: MusicId | null) {
    this.wantMusic = id;
    if (!this.active() || !this.ctx) return;
    if (this.music?.id === id) return;
    const c = this.ctx;
    const old = this.music;
    this.music = null;
    if (old) {
      window.clearTimeout(old.timer);
      old.gain.gain.cancelScheduledValues(c.currentTime);
      old.gain.gain.setValueAtTime(old.gain.gain.value, c.currentTime);
      old.gain.gain.linearRampToValueAtTime(0, c.currentTime + FADE);
      try {
        old.src.stop(c.currentTime + FADE + 0.1);
      } catch {
        /* already stopped */
      }
    }
    if (!id) return;
    void this.load('music', id).then((buf) => {
      if (!buf || this.wantMusic !== id || this.music?.id === id) return;
      this.startLoop(id, buf, FADE);
    });
  }

  /** Generated tracks are not seamless, so each pass overlaps the next with a short crossfade. */
  private startLoop(id: MusicId, buf: AudioBuffer, fadeIn: number) {
    const c = this.ctx!;
    const gain = c.createGain();
    gain.gain.setValueAtTime(0, c.currentTime);
    gain.gain.linearRampToValueAtTime(1, c.currentTime + fadeIn);
    const src = c.createBufferSource();
    src.buffer = buf;
    src.connect(gain).connect(this.musicBus);
    src.start();
    const passMs = Math.max(4, buf.duration - LOOP_XFADE) * 1000;
    const timer = window.setTimeout(() => {
      if (this.music?.src !== src) return;
      const t = c.currentTime;
      gain.gain.setValueAtTime(gain.gain.value, t);
      gain.gain.linearRampToValueAtTime(0, t + LOOP_XFADE);
      src.stop(t + LOOP_XFADE + 0.1);
      this.startLoop(id, buf, LOOP_XFADE);
    }, passMs);
    this.music = { id, gain, src, timer };
  }

  /** Is there a baked narrator recording for this exact line? */
  async hasLine(text: string) {
    const m = await (this.manifest ?? Promise.resolve(null));
    return !!m?.vo[lineHash(text)];
  }

  /** Speak a narrator line if it was baked; ducks the music under it. Returns false when there is no recording. */
  async playLine(text: string): Promise<boolean> {
    if (!this.active() || !this.manifest) return false;
    const hash = lineHash(text);
    if (this.voice?.hash === hash) return true;
    const m = await this.manifest;
    const entry = m?.vo[hash];
    if (!entry) return false;
    const buf = await this.load('vo', entry.id);
    if (!buf || !this.ctx || !getPrefs().sound) return false;
    this.stopVoice();
    const c = this.ctx;
    const src = c.createBufferSource();
    src.buffer = buf;
    src.connect(this.voiceBus);
    this.duckBus.gain.setTargetAtTime(DUCK, c.currentTime, 0.15);
    src.onended = () => {
      if (this.voice?.src === src) {
        this.voice = null;
        this.duckBus.gain.setTargetAtTime(1, c.currentTime, 0.4);
        this.emit();
      }
    };
    src.start(c.currentTime + 0.15);
    this.voice = { src, hash };
    this.emit();
    return true;
  }

  stopVoice() {
    if (!this.voice || !this.ctx) return;
    const v = this.voice;
    this.voice = null;
    try {
      v.src.stop();
    } catch {
      /* not started */
    }
    this.duckBus.gain.setTargetAtTime(1, this.ctx.currentTime, 0.3);
    this.emit();
  }

  status() {
    return { ctx: this.ctx?.state ?? 'none', ext: this.ext, music: this.music?.id ?? null, speaking: !!this.voice, loaded: [...this.buffers.keys()] };
  }

  get speaking() {
    return !!this.voice;
  }

  onChange(f: () => void) {
    this.listeners.add(f);
    return () => this.listeners.delete(f);
  }

  private emit() {
    this.listeners.forEach((f) => f());
  }
}

export const audio = new AudioEngine();

/** Read-only status for QA scripts (Playwright checks that sound actually runs after the gesture). */
if (typeof window !== 'undefined') {
  (window as unknown as { __pjAudio: () => unknown }).__pjAudio = () => audio.status();
}

/** Scene mood to music loop. */
export function musicForMood(mood: string): MusicId {
  if (mood === 'chaos') return 'chaos';
  if (mood === 'tense' || mood === 'dread') return 'tense';
  if (mood === 'triumph') return 'ending';
  return 'calm';
}
